import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import car from 'src/assets/images/car.png';
import { PERMISSION } from 'src/features/auth/types';
import { useGetPhotoUrl } from 'src/features/eventLog/api/useGetPhotoUrl';
import { EVENT_TYPE } from 'src/features/eventLog/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { actionMessages } from 'src/features/navigation/utils/messages';
import { useListActiveStays } from 'src/features/stay/api/useListActiveStays';
import { useListPastStays } from 'src/features/stay/api/useListPastStays';
import SidePanelAttribute from 'src/features/stay/components/sidePanel/SidePanelAttribute';
import { ActiveStay, PastStay, STAY_TYPE } from 'src/features/stay/types';
import { useGetVisitor } from 'src/features/visitor/api/useGetVisitor';
import ImageCarousel from 'src/features/visitor/components/carousel/ImageCarousel';
import CreateVisitorModalForm from 'src/features/visitor/components/form/CreateVisitorModalForm';
import { Visitor } from 'src/features/visitor/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { formatTimestampToCustomId } from 'src/features/visitor/utils/timestampToId';
import { useGetStayDuration } from 'src/hooks/useGetStayDuration';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import DropdownButtonGroup from 'src/registry/ui/DropdownButtonGroup';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { Translation } from 'src/types';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';

type VisitorSidePanelProps = {
    visitorId?: string;
    eventType?: EVENT_TYPE;
    photoId?: string;
};

export const VISITOR_SIDE_PANEL_NAME = 'visitor-side-panel';

const VisitorSidePanel = ({ visitorId, eventType, photoId }: VisitorSidePanelProps) => {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const { getDurationFromStartDateToEndDate } = useGetStayDuration();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const manageActiveStays = useHasPermissions([PERMISSION.MANAGE_ACTIVE_STAYS]);
    const showCreateVisitor =
        manageActiveStays &&
        (eventType === EVENT_TYPE.LPR_CAMERA_DETECTION || eventType === EVENT_TYPE.GATEWAY_PASS_NOT_ALLOWED);

    const { data: photoUrl, isLoading: isPhotoUrlLoading } = useGetPhotoUrl({
        parkingId,
        photoId: photoId ?? '',
        config: { enabled: !!photoId },
    });

    const translate = React.useCallback(
        (translations?: Translation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );
    const { data, isLoading } = useGetVisitor({
        parkingId,
        visitorId: visitorId || '',
        config: { enabled: !!visitorId },
    });

    const visitorType = React.useMemo(() => (data ? visitorTypeBadgeOptions[data.type] : null), [data]);

    // The stays of the visitor are asked for by the visitor, not by a search for the plate it was created
    // with: a stay is recorded under the plate as the camera read it for its own entrance, which is not
    // necessarily the one of the visitor
    const { data: activeStaysData, isLoading: activeStaysLoading } = useListActiveStays({
        parkingId,
        visitor: visitorId || '',
        limit: 1,
        config: { enabled: !!visitorId },
    });

    const { data: pastStaysData } = useListPastStays({
        parkingId,
        visitor: visitorId || '',
        limit: 1,
        config: { enabled: !!visitorId && !activeStaysData?.count && !isLoading && !activeStaysLoading },
        ordering: '-entry_time',
    });

    const translateZones = React.useCallback(
        (values?: Visitor['zones']) => {
            if (!values) return '-';
            const filteredTranslations = values.map(({ translations }) => translate(translations));
            return filteredTranslations.join(', ') || '-';
        },
        [translate],
    );

    const [stay, stayType]: [ActiveStay | PastStay | undefined | null, STAY_TYPE | null] = React.useMemo(() => {
        if (activeStaysData?.count !== 0) {
            return [activeStaysData?.results[0], STAY_TYPE.ACTIVE];
        }
        if (pastStaysData?.count !== 0) {
            return [pastStaysData?.results[0], STAY_TYPE.PAST];
        }
        return [null, null];
    }, [activeStaysData, pastStaysData]);

    const permissionActiveStay = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const permissionPastStay = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const permissionAccessEntity = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);

    const accessStayPermission = stayType === STAY_TYPE.ACTIVE ? permissionActiveStay : permissionPastStay;

    const parkingStayRerouteUrl = React.useMemo(() => {
        if (!stay?.id || !stayType) return undefined;

        return `/${PATH.VISITORS}/${stayType}-stay/dashboard/${stay?.id}`;
    }, [stay, stayType]);

    const stayGateway = (stay as ActiveStay)?.entry_gateway ?? (stay as PastStay)?.exit_gateway;
    const photoTitle = stayGateway?.translations?.find((gateway) => gateway.language === locale)?.name;

    const stayTime = stay?.entry_time ?? stay?.exit_time;
    const photoSubtitle = `${formatMessage(visitorMessages.pass)} ${formatTimestampToCustomId(stayTime)}`;

    return (
        <>
            {data ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{data?.main_identifier?.value}</DialogTitle>
                        {isLoading || !visitorType ? (
                            <Skeleton className="h-6 w-full max-w-48" />
                        ) : (
                            <Badge theme={visitorType.theme}>
                                {visitorType.icon && <FontAwesomeIcon icon={visitorType.icon} />}
                                {formatMessage(visitorType.label)}
                            </Badge>
                        )}
                    </div>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />
            <DialogBody>
                {photoUrl ? (
                    <ImageCarousel
                        imageSlides={[{ image: photoUrl.url, title: photoTitle ?? '', subtitle: photoSubtitle ?? '' }]}
                        isLoading={isPhotoUrlLoading}
                    />
                ) : (
                    <GeneralBlock className="hidden flex-1 items-center justify-center md:flex">
                        <img src={car} alt="Car" />
                    </GeneralBlock>
                )}
                {showCreateVisitor && <CreateVisitorModalForm open={isModalOpen} onOpenChange={setIsModalOpen} />}
                <DropdownButtonGroup dropdownButtonTitle="">
                    <Button
                        variant="outline"
                        className="cursor-pointer"
                        onClick={() => visitorId && navigate(`/${PATH.VISITORS}/visitor/${visitorId}`)}
                    >
                        {formatMessage(visitorMessages.openDashboard)}
                    </Button>
                    {showCreateVisitor && (
                        <Button className="cursor-pointer" variant="outline" onClick={() => setIsModalOpen(true)}>
                            {formatMessage(actionMessages.addVisitor)}
                        </Button>
                    )}
                </DropdownButtonGroup>

                <div className="mt-4 grid gap-4 sm:gap-6">
                    <SidePanelAttribute
                        label={formatMessage(
                            data?.in_parking ? visitorMessages.activeVisit : visitorMessages.lastVisit,
                        )}
                        value={stay?.ref ?? '-'}
                        link={parkingStayRerouteUrl && accessStayPermission ? parkingStayRerouteUrl : ''}
                        className="flex flex-col gap-1"
                    />

                    <SidePanelAttribute
                        label={formatMessage(visitorMessages.enteredAt)}
                        value={getFormattedDateTimeForDetailPanel(stay?.entry_time)}
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(visitorMessages.exitedAt)}
                        value={getFormattedDateTimeForDetailPanel(stay?.exit_time)}
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(visitorMessages.duration)}
                        value={getDurationFromStartDateToEndDate({
                            startDate: stay?.entry_time,
                            endDate: stay?.exit_time,
                        })}
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(visitorMessages.identifiers)}
                        value={stay?.main_identifier?.value ?? '-'}
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(visitorMessages.owner)}
                        value={data?.owner?.name ?? '-'}
                        link={
                            permissionAccessEntity && data?.owner
                                ? `/${PATH.ENTITIES}/${data?.owner.type}/${data?.owner?.id}`
                                : undefined
                        }
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(visitorMessages.parkingZone)}
                        value={translate(stay?.period_zone?.translations) ?? '-'}
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(visitorMessages.visitedZones)}
                        value={translateZones(data?.zones) ?? '-'}
                        className="flex flex-col gap-1"
                    />
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default VisitorSidePanel;
